import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Search,
  BookOpen,
  Send,
  Sliders,
  Award,
  LayoutDashboard,
} from 'lucide-react';

interface FAQItemData {
  q: string;
  a: string;
  tag?: string;
}

interface FAQCategory {
  category: string;
  icon: any;
  questions: FAQItemData[];
}

const faqCategories: FAQCategory[] = [
  {
    category: 'General & Industrial Methodology',
    icon: BookOpen,
    questions: [
      {
        q: 'What is InsuScan AI and what engineering problem does it solve?',
        a: 'InsuScan AI is an industrial asset integrity decision-support system designed to solve the €12M+/facility challenge of Corrosion Under Insulation (CUI). By synthesizing non-intrusive NDT screening (passive radiometric thermography, optical cladding segmentation, and pulsed eddy current grids) with deterministic API 579-1 Level 1 engineering math, InsuScan eliminates 40% to 65% of unnecessary insulation stripping and expensive scaffolding while focusing maintenance crews on high-risk degradation.',
        tag: 'CORE CONCEPT',
      },
      {
        q: 'Who is the platform certified for?',
        a: 'InsuScan AI is engineered specifically for Asset Integrity Managers, Chief Corrosion Engineers, Plant Reliability Superintendents, and certified field inspectors (API 510, API 570, API 653, NACE/AMPP, ASNT Level III) operating across oil refining, petrochemicals, LNG terminals, offshore platforms, and heavy chemical processing plants.',
        tag: 'TARGET AUDIENCE',
      },
      {
        q: 'Does InsuScan replace certified inspection personnel?',
        a: 'Never. InsuScan AI operates strictly under an engineer-in-the-loop paradigm. In accordance with API 570 and OSHA 1910.119 Process Safety Management (PSM), all automated defect segmentations and fitness-for-service dispositions require verification and digital authorization by an API-certified inspector or registered Professional Engineer (PE).',
        tag: 'HUMAN-IN-THE-LOOP',
      },
    ],
  },
  {
    category: 'NDT Screening & Thermography Physics',
    icon: Sliders,
    questions: [
      {
        q: 'Can infrared thermography detect corrosion directly under insulation?',
        a: 'No, and maintaining this physical distinction is fundamental to engineering integrity. Infrared thermography measures surface radiance and temperature differentials (ΔT) caused by the elevated heat capacitance of wetted insulation. It detects trapped moisture, which is the primary causal agent of CUI. Volumetric wall loss is subsequently verified using non-intrusive Pulsed Eddy Current (PEC) or Radiographic Testing (RTR).',
        tag: 'PHYSICS OF IR',
      },
      {
        q: 'What cladding defect classes are identified by the vision model?',
        a: 'The fine-tuned YOLOv8x-Seg and SAM-2 models detect six critical failure classes: (1) Longitudinal and circumferential weather jacketing seam openings, (2) Caulking and elastomeric sealant deterioration, (3) Mechanical punctures and cladding tears, (4) Banding strap corrosion or breakage, (5) Gravity-inverted lap installations, and (6) Rust bleed-through weeping stains.',
        tag: 'COMPUTER VISION',
      },
      {
        q: 'How does Pulsed Eddy Current (PEC) screening work through cladding?',
        a: 'PEC induces transient electromagnetic eddy currents into the conductive carbon steel pipe wall through both thermal insulation (calcium silicate, mineral wool, aerogel) and protective weather cladding (aluminum or stainless steel). The decay rate of the secondary magnetic field directly correlates with the remaining wall thickness without stripping insulation.',
        tag: 'PEC NDT',
      },
    ],
  },
  {
    category: 'Engineering Standards & Risk Prioritization',
    icon: Award,
    questions: [
      {
        q: 'How does the API 579-1 Part 5 Level 1 Calculator operate?',
        a: 'The calculator implements the exact deterministic formulas from API 579-1 / ASME FFS-1 Part 5 for local metal loss. It calculates the parameter λ = 1.285 · s / √(D_o · t_min), evaluates the Folias curvature bulging factor M_t, computes the Remaining Strength Factor RSF = R_t / (1 - (1 - R_t) / M_t), and compares RSF against RSF_allow (0.90) to determine whether the component passes at full MAWP or requires immediate derating.',
        tag: 'API 579-1 FFS',
      },
      {
        q: 'What is the Inspection Priority Score (IPS) methodology?',
        a: 'IPS is a multi-barrier risk ranking score (0.0 to 10.0) aligned with API RP 581 and DNV-RP-G109. It aggregates five weighted vectors: Thermal Susceptibility (30%, evaluated across the peak CUI window of -4°C to 175°C), Geometric Traps (25%, dead legs, low points, unsealed pipe shoes), Environmental Exposure (20%, ISO 9223 C1-CX marine/industrial), Historical Wall Degradation (15%), and Inspection Confidence (10%). Circuits scoring ≥ 7.0 are mandated for immediate screening.',
        tag: 'API 581 / DNV-RP-G109',
      },
      {
        q: 'How are legacy turnaround inspection reports digitized?',
        a: 'The PDF Parser utilizes LayoutLMv3 with multimodal vision models to scan unstructured turnaround logs, UT thickness tables, and isometric sketches. It extracts tag identifiers, CML thickness measurements, inspection dates, and corrosion rates, generating structured CSV datasets ready for RBI database ingestion.',
        tag: 'AI DIGITIZATION',
      },
    ],
  },
  {
    category: 'Enterprise Integration, CMMS & Security',
    icon: Send,
    questions: [
      {
        q: 'How does InsuScan AI connect with SAP Plant Maintenance (PM) and IBM Maximo?',
        a: 'InsuScan AI supports bidirectional enterprise CMMS integrations via RFC/BAPI (BAPI_ALM_NOTIF_CREATE for SAP PM) and standard REST APIs for IBM Maximo. Each dispatched work order automatically includes the complete digital evidence dossier: calibrated optical photos, FLIR thermograms, PEC thickness matrices, and the signed API 579 assessment summary.',
        tag: 'CMMS DISPATCH',
      },
      {
        q: 'How is industrial cybersecurity and data confidentiality handled?',
        a: 'InsuScan AI complies with IEC 62443-4-2 Security Level 3, enforces TLS 1.3 encryption in transit, and offers on-premise air-gapped containerized deployments for classified sovereign energy installations. All inspection records and engineering approvals are sealed with SHA-256 cryptographic provenance hashes.',
        tag: 'CYBERSECURITY',
      },
    ],
  },
];

function FAQAccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItemData;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={`border rounded-xl transition-all ${
        isOpen
          ? 'bg-white border-blue-300 shadow-xs ring-1 ring-blue-100'
          : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 sm:p-5 text-left cursor-pointer gap-4"
        onClick={onToggle}
      >
        <div className="space-y-1">
          {item.tag && (
            <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold inline-block">
              {item.tag}
            </span>
          )}
          <h3 className="font-bold text-sm sm:text-base text-slate-900 leading-snug">
            {item.q}
          </h3>
        </div>
        <div
          className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
            isOpen ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>
      {isOpen && (
        <div className="px-4 sm:px-5 pb-5 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100 mt-1 font-sans">
          {item.a}
        </div>
      )}
    </div>
  );
}

export function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({
    '0-0': true,
    '1-0': true,
  });

  const toggleItem = (catIdx: number, qIdx: number) => {
    const key = `${catIdx}-${qIdx}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = faqCategories
    .map((cat, catIdx) => {
      const filteredQuestions = cat.questions.filter(
        (q) =>
          q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.a.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (q.tag && q.tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      return { ...cat, originalIdx: catIdx, questions: filteredQuestions };
    })
    .filter((cat) => cat.questions.length > 0);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-20">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400 active:scale-[0.98] transition-all shadow-xs cursor-pointer group"
              >
                <ArrowLeft className="h-3.5 w-3.5 text-slate-500 group-hover:text-slate-900 group-hover:-translate-x-0.5 transition-all" />
                <span>Back to Home</span>
              </Link>
              <div className="h-5 w-px bg-slate-300" />
              <span className="text-sm font-semibold text-slate-900">
                Knowledge Base & FAQ
              </span>
            </div>
            <Link
              to="/app"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm hover:shadow-md hover:shadow-blue-500/25 border border-blue-500 cursor-pointer"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              <span>Open Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="bg-gradient-to-b from-white to-slate-100/70 border-b border-slate-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-wider">
            <HelpCircle className="h-3.5 w-3.5" />
            Engineering Knowledge Base
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-base text-slate-600 max-w-xl mx-auto">
            Find technical answers regarding CUI physics, API 579-1 FFS mathematics, inspection codes, and enterprise CMMS integrations.
          </p>

          {/* Search Box */}
          <div className="relative max-w-xl mx-auto pt-4">
            <Search className="absolute left-4 top-1/2 mt-2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by keyword (e.g., API 579, RSF, FLIR, SAP, PEC, CUI window)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-300 bg-white text-sm shadow-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 space-y-12">
        {filteredCategories.length === 0 ? (
          <div className="tech-panel p-8 text-center bg-white rounded-xl border border-slate-200">
            <p className="text-slate-500 text-sm">No matching questions found for "{searchTerm}".</p>
            <button
              onClick={() => setSearchTerm('')}
              className="btn-secondary btn-sm mt-3"
            >
              Clear Search Query
            </button>
          </div>
        ) : (
          filteredCategories.map((cat) => {
            const Icon = cat.icon;
            return (
              <section key={cat.category} className="space-y-4">
                <div className="flex items-center gap-2.5 pb-2 border-b border-slate-200">
                  <Icon className="h-5 w-5 text-blue-600" />
                  <h2 className="text-base sm:text-lg font-bold text-slate-900">
                    {cat.category}
                  </h2>
                  <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold ml-auto">
                    {cat.questions.length} {cat.questions.length === 1 ? 'topic' : 'topics'}
                  </span>
                </div>

                <div className="space-y-3">
                  {cat.questions.map((item, qIdx) => {
                    const key = `${cat.originalIdx}-${qIdx}`;
                    return (
                      <FAQAccordionItem
                        key={qIdx}
                        item={item}
                        isOpen={!!openItems[key]}
                        onToggle={() => toggleItem(cat.originalIdx, qIdx)}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })
        )}

        {/* Contact Support Banner */}
        <section className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-blue-950 text-white border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-md">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg font-bold">Require Custom Engineering Consultation?</h3>
            <p className="text-xs sm:text-sm text-slate-300 max-w-md">
              Our staff includes API-certified inspection engineers and NDT Level III specialists available for facility-specific pilot integrations.
            </p>
          </div>
          <a
            href="mailto:support@insuscan.ai"
            className="btn-primary shrink-0 inline-flex items-center gap-2 cursor-pointer shadow-md"
          >
            <Send className="h-4 w-4" />
            Contact Integrity Support
          </a>
        </section>
      </main>
    </div>
  );
}
